import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Container,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  RocketLaunch as RocketIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const steps = ['Seleccionar Plan', 'Tu Empresa WISP', 'Cuenta Administrador'];

const TenantSignup = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Datos del formulario
  const [formData, setFormData] = useState({
    wisp_name: '',
    slug: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_phone: '',
    plan_type: 'pro'
  });

  // Verificación de slug en tiempo real
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // { available: boolean, message: string }

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Debounce para chequear disponibilidad de subdominio
  useEffect(() => {
    if (!formData.slug || formData.slug.length < 3) {
      setSlugStatus(null);
      return;
    }

    const cleanSlug = formData.slug.toLowerCase().replace(/[^a-z0-9-]/g, '');
    const timer = setTimeout(async () => {
      setSlugChecking(true);
      try {
        const res = await fetch(`/api/auth/check-slug/${cleanSlug}`);
        const data = await res.json();
        setSlugStatus(data);
      } catch (err) {
        setSlugStatus({ available: false, message: 'Error de conexión al verificar subdominio' });
      } finally {
        setSlugChecking(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.slug]);

  const handleNext = () => {
    setError('');
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!formData.wisp_name) {
        setError('El nombre de tu empresa WISP es obligatorio');
        return;
      }
      if (!formData.slug || formData.slug.length < 3) {
        setError('El subdominio debe tener al menos 3 caracteres');
        return;
      }
      if (slugStatus && !slugStatus.available) {
        setError('Por favor elige un subdominio que esté disponible');
        return;
      }
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.admin_email || !formData.admin_password) {
      setError('Correo y contraseña son requeridos');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/auth/register-tenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al completar el registro');

      // Guardar token e ingresar inmediatamente
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setSuccessMessage(data.message);

      setTimeout(() => {
        navigate('/');
      }, 1800);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2
      }}
    >
      <Container maxWidth="md">
        {/* Logo / Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h3"
            fontWeight="900"
            sx={{
              background: 'linear-gradient(90deg, #38bdf8 0%, #818cf8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-1px'
            }}
          >
            YinoWISP SaaS
          </Typography>
          <Typography variant="subtitle1" color="grey.400" mt={1}>
            Crea tu propia plataforma de gestión WISP autónoma en menos de 2 minutos.
          </Typography>
        </Box>

        <Paper
          elevation={12}
          sx={{
            borderRadius: 4,
            p: { xs: 3, md: 5 },
            bgcolor: 'rgba(30, 41, 59, 0.7)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#ffffff'
          }}
        >
          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': { color: 'grey.300', fontWeight: 'bold' },
                    '& .MuiStepLabel-label.Mui-active': { color: '#38bdf8' },
                    '& .MuiStepLabel-label.Mui-completed': { color: '#4ade80' },
                    '& .MuiSvgIcon-root': { color: 'grey.600' },
                    '& .MuiSvgIcon-root.Mui-active': { color: '#38bdf8' },
                    '& .MuiSvgIcon-root.Mui-completed': { color: '#4ade80' }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
          {successMessage && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2, fontSize: '1.05rem', fontWeight: 'bold' }}>
              {successMessage}
            </Alert>
          )}

          {/* PASO 1: Selección de Plan */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" textAlign="center" mb={3}>
                🚀 Elige el Plan Ideal para tu Empresa WISP
              </Typography>
              <Grid container spacing={3}>
                {/* Plan Emprendedor */}
                <Grid item xs={12} md={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, plan_type: 'emprendedor' })}
                    sx={{
                      borderRadius: 3,
                      cursor: 'pointer',
                      border: formData.plan_type === 'emprendedor' ? '2px solid #38bdf8' : '1px solid rgba(255,255,255,0.1)',
                      bgcolor: formData.plan_type === 'emprendedor' ? 'rgba(56, 189, 248, 0.1)' : 'rgba(15, 23, 42, 0.6)',
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-4px)' }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Chip label="INICIO" color="info" size="small" sx={{ mb: 1, fontWeight: 'bold' }} />
                      <Typography variant="h6" fontWeight="bold" color="#ffffff">Emprendedor</Typography>
                      <Typography variant="h4" fontWeight="800" color="#38bdf8" my={1}>S/ 50 <Typography component="span" variant="caption" color="grey.400">/mes</Typography></Typography>
                      <Typography variant="body2" color="grey.300">Hasta 100 Clientes</Typography>
                      <Typography variant="body2" color="grey.300">2 Routers MikroTik</Typography>
                      <Typography variant="caption" color="grey.400" display="block" mt={1}>7 días de prueba gratis</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plan Profesional (Recomendado) */}
                <Grid item xs={12} md={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, plan_type: 'pro' })}
                    sx={{
                      borderRadius: 3,
                      cursor: 'pointer',
                      position: 'relative',
                      border: formData.plan_type === 'pro' ? '2px solid #818cf8' : '1px solid rgba(255,255,255,0.1)',
                      bgcolor: formData.plan_type === 'pro' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(15, 23, 42, 0.6)',
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-4px)' }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Chip label="MÁS POPULAR" color="primary" size="small" sx={{ mb: 1, fontWeight: 'bold' }} />
                      <Typography variant="h6" fontWeight="bold" color="#ffffff">Profesional</Typography>
                      <Typography variant="h4" fontWeight="800" color="#818cf8" my={1}>S/ 120 <Typography component="span" variant="caption" color="grey.400">/mes</Typography></Typography>
                      <Typography variant="body2" color="grey.300">Hasta 500 Clientes</Typography>
                      <Typography variant="body2" color="grey.300">5 Routers MikroTik</Typography>
                      <Typography variant="caption" color="grey.400" display="block" mt={1}>7 días de prueba gratis</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plan Enterprise */}
                <Grid item xs={12} md={4}>
                  <Card
                    onClick={() => setFormData({ ...formData, plan_type: 'enterprise' })}
                    sx={{
                      borderRadius: 3,
                      cursor: 'pointer',
                      border: formData.plan_type === 'enterprise' ? '2px solid #c084fc' : '1px solid rgba(255,255,255,0.1)',
                      bgcolor: formData.plan_type === 'enterprise' ? 'rgba(192, 132, 252, 0.1)' : 'rgba(15, 23, 42, 0.6)',
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-4px)' }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Chip label="ILIMITADO" color="secondary" size="small" sx={{ mb: 1, fontWeight: 'bold' }} />
                      <Typography variant="h6" fontWeight="bold" color="#ffffff">Enterprise</Typography>
                      <Typography variant="h4" fontWeight="800" color="#c084fc" my={1}>S/ 250 <Typography component="span" variant="caption" color="grey.400">/mes</Typography></Typography>
                      <Typography variant="body2" color="grey.300">Hasta 2,000 Clientes</Typography>
                      <Typography variant="body2" color="grey.300">15 Routers MikroTik</Typography>
                      <Typography variant="caption" color="grey.400" display="block" mt={1}>7 días de prueba gratis</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 'bold', textTransform: 'none' }}
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          )}

          {/* PASO 2: Datos de la Empresa WISP & Subdominio */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                🏢 Datos de tu Empresa WISP
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Comercial de tu WISP"
                    value={formData.wisp_name}
                    onChange={(e) => setFormData({ ...formData, wisp_name: e.target.value })}
                    placeholder="Ej: Alfa Telecom Soluciones"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subdominio Deseado"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="Ej: alfawisp"
                    required
                    helperText={
                      slugChecking ? 'Verificando disponibilidad...' :
                      slugStatus ? (
                        <Typography variant="caption" color={slugStatus.available ? '#4ade80' : '#f87171'} fontWeight="bold">
                          {slugStatus.message}
                        </Typography>
                      ) : 'Tu panel estará accesible en https://[tu-subdominio].yinowisp.com'
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LanguageIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          {slugChecking ? <CircularProgress size={20} color="inherit" /> :
                           slugStatus?.available ? <Chip label="DISPONIBLE" color="success" size="small" /> :
                           slugStatus?.available === false ? <Chip label="OCUPADO" color="error" size="small" /> : null}
                        </InputAdornment>
                      ),
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="space-between" mt={4}>
                <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ color: 'grey.300', textTransform: 'none' }}>
                  Atrás
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  disabled={!formData.wisp_name || !formData.slug || (slugStatus && !slugStatus.available)}
                  sx={{ borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 'bold', textTransform: 'none' }}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          )}

          {/* PASO 3: Cuenta de Administrador */}
          {activeStep === 2 && (
            <form onSubmit={handleSubmit}>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                👤 Crear Cuenta de Administrador
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Completo del Administrador"
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    placeholder="Ej: Juan Perez"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Correo Electrónico"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    placeholder="juan@empresa.com"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono WhatsApp"
                    value={formData.admin_phone}
                    onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                    placeholder="+51 987654321"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Contraseña de Acceso"
                    value={formData.admin_password}
                    onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: 'grey.400' }} /></InputAdornment>,
                      sx: { color: '#ffffff', bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: 2 }
                    }}
                    InputLabelProps={{ sx: { color: 'grey.300' } }}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="space-between" mt={4}>
                <Button onClick={handleBack} disabled={loading} startIcon={<ArrowBackIcon />} sx={{ color: 'grey.300', textTransform: 'none' }}>
                  Atrás
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RocketIcon />}
                  sx={{ borderRadius: 2.5, px: 5, py: 1.3, fontWeight: 'bold', textTransform: 'none', fontSize: '1.05rem' }}
                >
                  {loading ? 'Creando tu WISP...' : '¡Iniciar mi WISP Gratis!'}
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TenantSignup;
